import React from 'react';
import { Box, List, ListItemButton, ListItemText, Typography } from '@mui/material';
import {
  PRIORITY_DOMAIN_NAMES,
  getPrioritySchema,
  resolveCardTitle,
  type DomainGroup,
  type FieldDef,
} from '@brainpool/profile-schema';
import { DomainGroupViewer } from './DomainGroupViewer';
import { formatDomainName } from './parsers';

export interface DomainDataViewerProps {
  domainGroups: DomainGroup[];
  /** If true, renders a placeholder when there is no data. Default: true */
  showEmpty?: boolean;
}

export function DomainDataViewer({ domainGroups, showEmpty = true }: DomainDataViewerProps) {
  const columnMaxHeight = { xs: 320, md: 560 };
  const sidebarRef = React.useRef<HTMLDivElement | null>(null);
  const contentRef = React.useRef<HTMLDivElement | null>(null);
  const scrollSnapshotRef = React.useRef<{
    pageX: number;
    pageY: number;
    sidebarTop: number;
    contentTop: number;
  } | null>(null);

  const hasRecordData = (domain: string, record: Record<string, unknown>): boolean => {
    const schema = getPrioritySchema(domain);
    if (!schema) return false;

    const fields = schema.fields as Record<string, FieldDef | undefined>;
    const hasVisibleLayoutValue = schema.layout
      .flatMap(r => r)
      .some(cell => {
        const def = fields[cell.field];
        if (!def || def.hidden) return false;
        const val = record[cell.field];
        return val !== null && val !== undefined && val !== '';
      });

    if (hasVisibleLayoutValue) return true;

    const cardTitle = resolveCardTitle(schema, record);
    return Boolean(cardTitle && cardTitle.trim() !== '');
  };

  const sectionsWithData = PRIORITY_DOMAIN_NAMES.filter(domain => {
    const records = domainGroups.find(g => g.domain === domain)?.records ?? [];
    return records.some(record => hasRecordData(domain, record));
  });

  const [selectedDomain, setSelectedDomain] = React.useState(
    sectionsWithData[0] ?? PRIORITY_DOMAIN_NAMES[0]
  );

  React.useEffect(() => {
    if (sectionsWithData.length > 0 && !sectionsWithData.includes(selectedDomain)) {
      setSelectedDomain(sectionsWithData[0]);
    }
  }, [sectionsWithData, selectedDomain]);

  React.useEffect(() => {
    if (!scrollSnapshotRef.current) return;
    const snapshot = scrollSnapshotRef.current;
    scrollSnapshotRef.current = null;

    requestAnimationFrame(() => {
      try {
        window.scrollTo(snapshot.pageX, snapshot.pageY);
      } catch {
        // no-op for non-browser environments
      }

      if (sidebarRef.current) sidebarRef.current.scrollTop = snapshot.sidebarTop;
      if (contentRef.current) contentRef.current.scrollTop = snapshot.contentTop;
    });
  }, [selectedDomain]);

  const handleSelectDomain = React.useCallback(
    (domain: string) => {
      if (domain === selectedDomain) return;

      scrollSnapshotRef.current = {
        pageX: window.scrollX,
        pageY: window.scrollY,
        sidebarTop: sidebarRef.current?.scrollTop ?? 0,
        contentTop: contentRef.current?.scrollTop ?? 0,
      };

      setSelectedDomain(domain);
    },
    [selectedDomain]
  );

  if (sectionsWithData.length === 0) {
    if (!showEmpty) return null;
    return (
      <Typography variant="body2" sx={{ color: 'text.disabled', fontStyle: 'italic', py: 2 }}>
        No profile data available yet.
      </Typography>
    );
  }

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: '260px minmax(0, 1fr)' },
        gap: 2,
        minHeight: 0,
      }}
    >
      <Box
        ref={sidebarRef}
        sx={{
          border: '1px solid',
          borderColor: 'grey.200',
          borderRadius: 1,
          backgroundColor: 'background.paper',
          maxHeight: columnMaxHeight,
          overflowY: 'auto',
        }}
      >
        <List disablePadding>
          {sectionsWithData.map(domain => (
            <ListItemButton
              key={domain}
              selected={selectedDomain === domain}
              onClick={() => handleSelectDomain(domain)}
              sx={{
                py: 1,
                '&.Mui-selected': {
                  backgroundColor: 'primary.main',
                  color: 'primary.contrastText',
                },
                '&.Mui-selected:hover': {
                  backgroundColor: 'primary.dark',
                },
              }}
            >
              <ListItemText
                primary={formatDomainName(domain)}
                primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
              />
              <Box
                sx={{
                  backgroundColor:
                    selectedDomain === domain ? 'rgba(255, 255, 255, 0.2)' : 'primary.main',
                  color: 'white',
                  borderRadius: '10px',
                  px: 0.75,
                  lineHeight: 1.6,
                  fontSize: '0.7rem',
                  fontWeight: 700,
                  minWidth: 20,
                  textAlign: 'center',
                }}
              >
                {domainGroups.find(g => g.domain === domain)?.records.length ?? 0}
              </Box>
            </ListItemButton>
          ))}
        </List>
      </Box>

      <Box ref={contentRef} sx={{ maxHeight: columnMaxHeight, overflowY: 'auto', minWidth: 0 }}>
        <DomainGroupViewer
          domain={selectedDomain}
          records={domainGroups.find(g => g.domain === selectedDomain)?.records ?? []}
          defaultExpanded
          collapsible={false}
        />
      </Box>
    </Box>
  );
}
